import React, {
  useState,
  forwardRef,
  useCallback,
  useRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import { RichTextPropTypes } from '../../../prop-types';
import RichTextBase from '../../rich-text/base';
import FieldLabel from '../field/label';

// Styles
import {
  Label,
  RatingLabelsWrapper,
  RatingLabel,
  StarSVG,
  RadioInput,
  Fieldset,
  RadioGroup,
} from './styles';
import iconSrc from '../../../utils/iconSrc';

const removeNumberAndHyphen = (str) => str?.replace(/^\d+\s*-\s*/, '');

const StarRating = forwardRef(
  (
    {
      error,
      name,
      options,
      description,
      value,
      defaultValue,
      id,
      onChange,
      labelProps,
      ...rest
    },
    ref
  ) => {
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);

    const radiobuttonRefs = useRef([]);

    useImperativeHandle(ref, () => ({
      get radioButtons() {
        return radiobuttonRefs.current;
      },
    }));

    const { label: smallestLabel, value: smallestValue } = options[0];
    const { label: largestLabel, value: largestValue } = options.at(-1);

    const formattedSmallestLabel = removeNumberAndHyphen(smallestLabel);
    const formattedLargestLabel = removeNumberAndHyphen(largestLabel);

    const changeHandler = useCallback(
      (event) => {
        setRating(event.currentTarget.value);

        if (onChange) {
          onChange(event);
        }
      },
      [onChange]
    );

    const getAriaLabel = (currentValue) => {
      const additionalLabel = () => {
        if (currentValue === smallestValue) {
          return `. ${formattedSmallestLabel}`;
        }

        if (currentValue === largestValue) {
          return `. ${formattedLargestLabel}`;
        }

        return '';
      };

      return `${currentValue} out of ${largestValue} stars${additionalLabel()}`;
    };

    return (
      <Fieldset>
        <FieldLabel
          {...labelProps}
          as="legend"
          labelHiddenText={`Select an option from ${smallestValue} to ${largestValue}, with ${smallestValue} being ${formattedSmallestLabel} and ${largestValue} being ${formattedLargestLabel}`}
        />
        {description && <RichTextBase richText={description} />}

        <RadioGroup>
          {options.map((option, index) => (
            <Label
              key={`${id}-${index}`}
              for={`${id}-${index}`}
              aria-label={getAriaLabel(option.value)}
              onMouseEnter={() => setHoverRating(option.value)}
              onMouseLeave={() => setHoverRating(0)}
            >
              <RadioInput
                type="radio"
                name={name}
                ref={(radioButton) => {
                  radiobuttonRefs.current[index] = radioButton;
                }}
                id={`${id}-${index}`}
                value={option.value}
                onClick={changeHandler}
                checked={rating === option.value}
                disabled={option.disabled}
                {...rest}
              />

              <StarSVG
                aria-hidden="true"
                $isSelected={option.value <= (hoverRating || rating)}
                src={iconSrc('star')}
              />
            </Label>
          ))}
        </RadioGroup>
        <RatingLabelsWrapper>
          <RatingLabel aria-hidden="true">{formattedSmallestLabel}</RatingLabel>
          <RatingLabel aria-hidden="true">{formattedLargestLabel}</RatingLabel>
        </RatingLabelsWrapper>
      </Fieldset>
    );
  }
);

export default StarRating;

StarRating.displayName = 'StarRating';

StarRating.propTypes = {
  error: PropTypes.bool,
  name: PropTypes.string,
  description: RichTextPropTypes,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      content: PropTypes.element,
    })
  ).isRequired,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  id: PropTypes.string,
  onChange: PropTypes.func,
  legend: PropTypes.string,
  labelProps: PropTypes.object,
  readOnly: PropTypes.bool,
};
